import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Check, ShareNetwork, UsersThree, User, FilePdf, Image as ImageIcon, File as FileIcon } from "@phosphor-icons/react";

/**
 * Forwards a message (or a single attachment out of a message) to one or
 * more teammates / groups.
 *
 * Props
 * -----
 *   msg          — the source message (required). Contains the id + all
 *                  existing attachments.
 *   attachment   — OPTIONAL. When provided, only that attachment is
 *                  forwarded (via the backend's `attachment_paths` filter,
 *                  iter158) and the source-message body is dropped so the
 *                  forwarder's own comment is the message body. When
 *                  omitted, the legacy full-message forward runs.
 *   onClose      — cancel handler (no-op mutation on cancel).
 *   onDone       — success handler (invoked exactly once, even on
 *                  duplicate-click storms — the submit button is disabled
 *                  while `busy=true`).
 */
export default function ForwardModal({ msg, attachment, onClose, onDone }) {
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [comment, setComment] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([api.get("/team-chat/members"), api.get("/team-chat/groups")])
      .then(([m, g]) => { setMembers(m.data); setGroups(g.data); })
      .catch(e => setErr(e.message));
  }, []);

  const toggleUser = (id) => setSelectedUsers(s => { const ns = new Set(s); ns.has(id) ? ns.delete(id) : ns.add(id); return ns; });
  const toggleGroup = (id) => setSelectedGroups(s => { const ns = new Set(s); ns.has(id) ? ns.delete(id) : ns.add(id); return ns; });

  const submit = async () => {
    if (selectedUsers.size === 0 && selectedGroups.size === 0) { setErr("Pick at least one recipient"); return; }
    if (busy) return;                                             // dedup double-click
    setBusy(true); setErr("");
    try {
      const payload = {
        to_user_ids: [...selectedUsers],
        group_ids: [...selectedGroups],
        comment: comment.trim() || null,
      };
      // iter158 — per-attachment forward. Server filters src attachments
      // to just this one path and reuses the existing storage reference
      // (no re-upload, no re-encoding, no filename/size drift).
      if (attachment?.path) payload.attachment_paths = [attachment.path];
      await api.post(`/team-chat/messages/${msg.id}/forward`, payload);
      onDone?.();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  // Header + source preview vary by mode.
  const isAttachmentMode = !!attachment;
  const attIcon = attachment?.content_type?.startsWith("image/")
    ? <ImageIcon size={14} />
    : attachment?.content_type === "application/pdf"
      ? <FilePdf size={14} />
      : <FileIcon size={14} />;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="forward-modal">
      <div className="bg-white w-full max-w-lg border border-[var(--border)] shadow-2xl flex flex-col max-h-[80vh]">
        <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-[var(--text-muted)] font-semibold">FORWARD</div>
            <div className="font-display font-bold text-[18px]">
              {isAttachmentMode ? "Send this attachment" : "Send this message"}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bg-sidebar)]" data-testid="forward-close"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3">
          {isAttachmentMode ? (
            <div className="flex items-center gap-2 border-l-2 border-[var(--accent)] pl-2 py-1 text-[12px]"
                 data-testid="forward-attachment-preview">
              {attIcon}
              <span className="flex-1 truncate">{attachment.original_filename || "attachment"}</span>
              <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                {attachment.content_type || ""}
                {attachment.size ? ` · ${(attachment.size / 1024).toFixed(0)} KB` : ""}
              </span>
            </div>
          ) : (
            <div className="text-[12px] text-[var(--text-secondary)] border-l-2 border-[var(--accent)] pl-2 italic" data-testid="forward-preview">
              <div className="font-semibold not-italic">{msg.from_user_name}</div>
              {msg.body || (msg.attachments?.length ? `${msg.attachments.length} attachment${msg.attachments.length === 1 ? "" : "s"}` : "—")}
            </div>
          )}

          {groups.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--text-muted)] mb-1.5">GROUPS</div>
              <div className="border border-[var(--border)]" data-testid="forward-groups">
                {groups.map(g => {
                  const checked = selectedGroups.has(g.id);
                  return (
                    <button key={g.id} type="button" onClick={() => toggleGroup(g.id)}
                            data-testid={`forward-group-${g.id}`}
                            className={`w-full flex items-center gap-2 px-3 py-2 border-b border-[var(--border)] hover:bg-[var(--bg-sidebar)] text-left ${checked ? "bg-[var(--bg-sidebar)]" : ""}`}>
                      <div className={`w-4 h-4 border-2 flex items-center justify-center ${checked ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)]"}`}>
                        {checked && <Check size={10} weight="bold" />}
                      </div>
                      <UsersThree size={14} />
                      <span className="text-[13px]">{g.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--text-muted)] mb-1.5">TEAMMATES</div>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name…"
              className="input w-full mb-1.5"
              data-testid="forward-search"
              maxLength={80}
            />
            <div className="border border-[var(--border)] max-h-[200px] overflow-y-auto" data-testid="forward-users">
              {(() => {
                const q = query.trim().toLowerCase();
                const filtered = q
                  ? members.filter(u => (u.name || "").toLowerCase().includes(q)
                                      || (u.username || "").toLowerCase().includes(q))
                  : members;
                if (filtered.length === 0) {
                  return <div className="px-3 py-3 text-[12px] text-[var(--text-muted)]" data-testid="forward-users-empty">No teammates match “{query}”.</div>;
                }
                return filtered.map(u => {
                const checked = selectedUsers.has(u.id);
                return (
                  <button key={u.id} type="button" onClick={() => toggleUser(u.id)}
                          data-testid={`forward-user-${u.id}`}
                          className={`w-full flex items-center gap-2 px-3 py-2 border-b border-[var(--border)] hover:bg-[var(--bg-sidebar)] text-left ${checked ? "bg-[var(--bg-sidebar)]" : ""}`}>
                    <div className={`w-4 h-4 border-2 flex items-center justify-center ${checked ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)]"}`}>
                      {checked && <Check size={10} weight="bold" />}
                    </div>
                    <User size={14} />
                    <span className="text-[13px] flex-1 truncate">{u.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{u.role}</span>
                  </button>
                );
                });
              })()}
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest font-semibold text-[var(--text-muted)] mb-1">ADD A COMMENT (optional)</label>
            <input value={comment} onChange={e => setComment(e.target.value)} placeholder="FYI..." className="input" data-testid="forward-comment" maxLength={500} />
          </div>

          {err && <div className="text-[12px] text-[var(--danger)]" data-testid="forward-error">{err}</div>}
        </div>
        <div className="border-t border-[var(--border)] px-5 py-3">
          <button onClick={submit} disabled={busy}
                  className="w-full bg-[var(--accent)] hover:opacity-90 disabled:opacity-50 text-white font-semibold py-2.5 flex items-center justify-center gap-2"
                  data-testid="forward-submit">
            <ShareNetwork size={14} weight="fill" />
            {busy ? "Forwarding..." : `Forward to ${selectedUsers.size + selectedGroups.size}`}
          </button>
        </div>
      </div>
    </div>
  );
}
